import { CHANNELS } from "../../shared/channels";
import { FeedComment, FeedItem, FeedKind } from "../../shared/achievements";
import { LANGUAGES, languageOfDeck } from "../../shared/languages";
import { StudySnapshot } from "../../shared/types";
import { call, mediaSrc } from "../bridge";
import { byId, flashToast } from "../dom";
import { locale, t } from "../i18n";
import { amOwner, canUse } from "../me";
import { awardsNow, loadAwards, onAwards } from "../awards_store";
import { isViewActive, onViewShown, showView } from "../navigation";
import { iconSvg } from "../icons";
import { pickImage } from "../image_pick";
import { cheerPost, commentOn, editPost, feedItems, isNew, lastSeen, loadFeed, markSeen, onFeed, postToFeed, removeCommentOn, removePost, watchFeed, whenLabel } from "../feed_store";

// the main feed: what people post, drew, unlocked and finished studying, and what happens between them.
// the home tile and widget reuse the same lines

const TEXT_MAX = 500;
const COMMENT_MAX = 300;

type Filter = FeedKind | "all" | "events";

let filter: Filter = "all";
let seenAtOpen = "";
let editing = "";
let picture = "";
// posts whose comments are showing, and the half written reply under each so a repaint doesn't lose it
const open = new Set<string>();
const drafts = new Map<string, string>();

const FILTERS: Array<{ id: Filter; label: string }> = [
  { id: "all", label: "all" },
  { id: "post", label: "posts" },
  { id: "drawing", label: "drawings" },
  { id: "achievement", label: "achievements" },
  { id: "study", label: "studying" },
  { id: "events", label: "events" }
];

const KIND_LABEL: Partial<Record<FeedKind, string>> = { joined: "joined", shared: "shared", achievement: "medal", drawing: "art", challenge: "art", study: "study" };

function el<K extends keyof HTMLElementTagNameMap>(tag: K, className = "", text = ""): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text) node.textContent = text;
  return node;
}

// "3 hours ago" in the person's language
export function ago(iso: string): string {
  const seconds = Math.round((new Date(iso).getTime() - Date.now()) / 1000);
  const rtf = new Intl.RelativeTimeFormat(locale(), { numeric: "auto" });
  const units: Array<[Intl.RelativeTimeFormatUnit, number]> = [["day", 86400], ["hour", 3600], ["minute", 60]];
  for (const [unit, size] of units) if (Math.abs(seconds) >= size) return rtf.format(Math.round(seconds / size), unit);
  return rtf.format(0, "second");
}

// the sentence each kind of entry says, built here so it follows the person's language
export function feedLine(item: FeedItem): string {
  const data = item.data;
  if (item.kind === "post") return data.text || "";
  if (item.kind === "joined") return t("joined Chi. say hi!");
  if (item.kind === "shared") {
    return data.what === "whiteboard" ? t("started a shared whiteboard: {title}", { title: data.title || "" }) : t("made a tier list public: {title}", { title: data.title || "" });
  }
  if (item.kind === "drawing") {
    const what = data.mode === "ref" ? t("drew a reference") : data.mode === "gesture" ? t("drew {n} gesture poses", { n: data.poses || 0 }) : t("drew {title}", { title: t(data.title || "") });
    return data.streak && data.streak > 1 ? `${what} · ${t("{n} day streak", { n: data.streak })}` : what;
  }
  if (item.kind === "challenge") return data.mode === "gesture" ? t("finished draw today with a gesture session") : data.mode === "ref" ? t("finished draw today with a reference") : t("finished draw today: {title}", { title: t(data.title || "") });
  if (item.kind === "achievement") {
    const names = (data.achievements || []).map((entry) => t(entry.name));
    const total = (data.achievements || []).reduce((sum, entry) => sum + entry.points, 0);
    const parts = [names.length ? t("unlocked {names}", { names: names.join(", ") }) : "", (data.medals || []).length ? t("earned {names}", { names: (data.medals || []).map((name) => t(name)).join(", ") }) : ""].filter(Boolean);
    return `${parts.join(" · ")}${total ? ` · +${total}` : ""}`;
  }
  const languages = (data.languages || []).join(", ");
  return [t("finished today's cards"), data.reviewed ? t("{n} answered", { n: data.reviewed }) : "", data.streak ? t("{n} day streak", { n: data.streak }) : "", languages].filter(Boolean).join(" · ");
}

function problem(error: unknown, fallback: string): void {
  flashToast(error instanceof Error ? t(error.message.replace(/^.*Error: /, "")) : t(fallback));
}

function pill(label: string, onClick: () => void, extra = ""): HTMLButtonElement {
  const button = el("button", `pill small ${extra}`.trim(), label);
  button.type = "button";
  button.addEventListener("click", onClick);
  return button;
}

function commentEl(item: FeedItem, comment: FeedComment): HTMLElement {
  const row = el("div", "feed-comment");
  const head = el("div", "feed-comment-head");
  head.append(el("b", "", comment.mine ? `${comment.name} · ${t("you")}` : comment.name), el("small", "feed-when", whenLabel(comment.at)));
  row.append(head, el("p", "feed-comment-text", comment.text));
  if (canUse("board", "edit") && (comment.mine || item.mine || amOwner())) {
    const remove = el("button", "feed-comment-remove", t("remove"));
    remove.type = "button";
    remove.addEventListener("click", () => void removeCommentOn(item.id, comment.id).catch((error) => problem(error, "couldn't remove that")));
    head.append(remove);
  }
  return row;
}

function commentsEl(item: FeedItem): HTMLElement {
  const box = el("div", "feed-comments");
  box.append(...item.comments.map((comment) => commentEl(item, comment)));
  if (canUse("board", "edit")) {
    const form = el("form", "feed-reply");
    const input = el("input");
    input.maxLength = COMMENT_MAX;
    input.placeholder = t("write a comment");
    input.setAttribute("aria-label", t("write a comment"));
    input.enterKeyHint = "send";
    input.autocomplete = "off";
    input.value = drafts.get(item.id) || "";
    input.addEventListener("input", () => drafts.set(item.id, input.value));
    const send = el("button", "pill small accent", t("send"));
    send.type = "submit";
    form.append(input, send);
    form.addEventListener("submit", async (event) => {
      event.preventDefault();
      if (!input.value.trim()) return input.focus();
      send.disabled = true;
      try {
        const text = input.value;
        drafts.delete(item.id);
        await commentOn(item.id, text);
      } catch (error) {
        drafts.set(item.id, input.value);
        problem(error, "couldn't post that");
        send.disabled = false;
      }
    });
    box.append(form);
  }
  return box;
}

function editorEl(item: FeedItem): HTMLElement {
  const form = el("form", "feed-edit");
  const input = el("textarea");
  input.rows = 3;
  input.maxLength = TEXT_MAX;
  input.value = item.data.text || "";
  input.setAttribute("aria-label", t("edit post"));
  const keys = el("div", "feed-foot");
  const save = el("button", "pill small accent", t("save"));
  save.type = "submit";
  keys.append(pill(t("cancel"), () => {
    editing = "";
    paint();
  }), save);
  form.append(input, keys);
  form.addEventListener("submit", async (event) => {
    event.preventDefault();
    try {
      editing = "";
      await editPost(item.id, input.value);
    } catch (error) {
      editing = item.id;
      problem(error, "couldn't save that");
    }
  });
  input.addEventListener("keydown", (event) => {
    if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) form.requestSubmit();
    if (event.key === "Escape") {
      editing = "";
      paint();
    }
  });
  requestAnimationFrame(() => {
    input.focus();
    input.setSelectionRange(input.value.length, input.value.length);
  });
  return form;
}

function itemEl(item: FeedItem): HTMLElement {
  const card = el("article", `feed-item feedk-${item.kind}${isNew(item, seenAtOpen) ? " fresh" : ""}`);
  card.dataset.id = item.id;
  const head = el("div", "feed-head");
  head.append(el("span", "pfp small", (Array.from(item.name.trim())[0] || "?").toUpperCase()), el("b", "feed-name", item.mine ? `${item.name} · ${t("you")}` : item.name));
  const label = KIND_LABEL[item.kind];
  if (label) head.append(el("span", "feed-kind", t(label)));
  head.append(el("small", "feed-when", whenLabel(item.at) + (item.editedAt ? ` · ${t("edited")}` : "")));
  card.append(head);
  const text = feedLine(item);
  if (editing === item.id && item.mine && item.kind === "post") card.append(editorEl(item));
  else if (text) card.append(el("p", `feed-text${item.kind === "post" ? " feed-post-text" : ""}`, text));
  if (item.image) {
    const frame = el("button", "feed-picture");
    frame.type = "button";
    frame.setAttribute("aria-label", t("open the picture"));
    const image = el("img");
    image.src = mediaSrc(item.image);
    image.alt = item.data.title || "";
    image.loading = "lazy";
    frame.append(image);
    if (item.data.ref) {
      const ref = el("img", "feed-ref");
      ref.src = mediaSrc(item.data.ref);
      ref.alt = t("the reference");
      ref.addEventListener("error", () => ref.remove());
      frame.append(ref);
    }
    frame.addEventListener("click", () => {
      byId<HTMLImageElement>("feed-lightbox-image").src = mediaSrc(item.image);
      byId("feed-lightbox").hidden = false;
    });
    card.append(frame);
  }
  if (item.data.note && item.kind === "drawing") card.append(el("p", "feed-note", item.data.note));
  const foot = el("div", "feed-foot");
  const cheer = el("button", `pill small${item.cheered ? " accent" : ""}`);
  cheer.type = "button";
  cheer.setAttribute("aria-pressed", String(item.cheered));
  cheer.innerHTML = `${iconSvg("star", 16)}<span></span>`;
  cheer.querySelector("span")!.textContent = item.cheers ? `${t("cheer")} · ${item.cheers}` : t("cheer");
  cheer.disabled = !canUse("board", "edit");
  cheer.addEventListener("click", () => void cheerPost(item.id).catch((error) => problem(error, "couldn't do that")));
  const talk = pill(item.comments.length ? `${t("comments")} · ${item.comments.length}` : t("comment"), () => {
    if (open.has(item.id)) open.delete(item.id);
    else open.add(item.id);
    paint();
  });
  talk.setAttribute("aria-expanded", String(open.has(item.id)));
  foot.append(cheer, talk);
  if (item.mine && item.kind === "post" && canUse("board", "edit") && editing !== item.id) {
    foot.append(pill(t("edit"), () => {
      editing = item.id;
      paint();
    }));
  }
  if ((item.mine || amOwner()) && canUse("board", "edit")) {
    foot.append(pill(t("remove"), () => {
      const question = item.mine ? t("delete your post? it goes for everyone") : t("delete {name}'s post? it goes for everyone", { name: item.name });
      if (!confirm(question)) return;
      void removePost(item.id).catch((error) => problem(error, "couldn't delete that"));
    }));
  }
  card.append(foot);
  if (open.has(item.id)) card.append(commentsEl(item));
  return card;
}

// the languages someone is studying with how far through each one's decks they are, plus the streak
async function paintProgress(): Promise<void> {
  const body = byId("feed-progress-body");
  const box = byId("feed-progress");
  let snapshot: StudySnapshot;
  try {
    snapshot = await call<StudySnapshot>(CHANNELS.japaneseToday);
  } catch {
    box.hidden = true;
    return;
  }
  box.hidden = false;
  const mine = awardsNow();
  const top = el("div", "feed-stats");
  const stat = (value: string, label: string) => {
    const cell = el("div", "feed-stat");
    cell.append(el("b", "", value), el("small", "", label));
    return cell;
  };
  top.append(
    stat(String(snapshot.streak), t("day streak")),
    stat(String(snapshot.today.reviewed), t("answered today")),
    stat(mine ? mine.points.toLocaleString(locale()) : "-", t("points"))
  );
  const studying = LANGUAGES.filter((language) => snapshot.decks.some((deck) => languageOfDeck(deck.deck) === language.id && deck.seen > 0));
  const rows = studying.map((language) => {
    const decks = snapshot.decks.filter((deck) => languageOfDeck(deck.deck) === language.id);
    const seen = decks.reduce((sum, deck) => sum + deck.seen, 0);
    const total = decks.reduce((sum, deck) => sum + deck.total, 0);
    const solid = decks.reduce((sum, deck) => sum + deck.mature, 0);
    const row = el("div", "feed-lang");
    const name = el("span", "feed-lang-name", language.native);
    name.lang = language.tag;
    const bar = el("span", "ladder-bar");
    const fill = el("i");
    fill.style.transform = `scaleX(${total ? seen / total : 0})`;
    bar.append(fill);
    row.append(name, bar, el("small", "hint", t("{seen} of {total} seen · {solid} solid", { seen, total, solid })));
    return row;
  });
  body.replaceChildren(top, ...(rows.length ? rows : [el("p", "hint", t("start a language in upskill and it shows up here"))]));
}

function shownItems(items: FeedItem[]): FeedItem[] {
  return items.filter((item) => {
    if (filter === "all") return true;
    if (filter === "events") return item.kind === "joined" || item.kind === "shared";
    return item.kind === filter || (filter === "drawing" && item.kind === "challenge");
  });
}

function paint(): void {
  const items = feedItems();
  byId("feed-filters").replaceChildren(...FILTERS.map((entry) => {
    const chip = el("button", `chip${filter === entry.id ? " active" : ""}`, t(entry.label));
    chip.type = "button";
    chip.addEventListener("click", () => {
      filter = entry.id;
      paint();
    });
    return chip;
  }));
  const shown = shownItems(items);
  byId("feed-list").replaceChildren(...shown.map(itemEl));
  byId("feed-empty").hidden = shown.length > 0;
  byId("feed-sub").textContent = items.length ? t("{n} posts", { n: items.length }) : "";
  // reading the feed live counts as having seen it
  if (isViewActive("feed")) markSeen();
}

function paintCounter(): void {
  const input = byId<HTMLTextAreaElement>("feed-input");
  const left = TEXT_MAX - input.value.length;
  const counter = byId("feed-left");
  counter.textContent = left < 120 ? String(left) : "";
  counter.classList.toggle("low", left < 30);
}

function paintPicture(): void {
  const preview = byId("feed-compose-picture");
  preview.hidden = !picture;
  const image = preview.querySelector("img");
  if (image) image.src = picture;
}

async function submit(event: Event): Promise<void> {
  event.preventDefault();
  const input = byId<HTMLTextAreaElement>("feed-input");
  if (!input.value.trim() && !picture) return input.focus();
  const send = byId<HTMLButtonElement>("feed-send");
  send.disabled = true;
  try {
    await postToFeed(input.value, picture);
    input.value = "";
    picture = "";
    paintCounter();
    paintPicture();
  } catch (error) {
    problem(error, "couldn't post that");
  } finally {
    send.disabled = false;
  }
}

export function setupFeedView(): void {
  if (!canUse("board")) return;
  watchFeed();
  onFeed(() => {
    if (isViewActive("feed")) paint();
  });
  const form = byId<HTMLFormElement>("feed-form");
  form.hidden = !canUse("board", "edit");
  form.addEventListener("submit", submit);
  const input = byId<HTMLTextAreaElement>("feed-input");
  input.maxLength = TEXT_MAX;
  input.addEventListener("input", paintCounter);
  input.addEventListener("keydown", (event) => {
    if (event.key === "Enter" && !event.shiftKey && !event.isComposing) {
      event.preventDefault();
      form.requestSubmit();
    }
  });
  const file = byId<HTMLInputElement>("feed-file");
  byId("feed-add-picture").addEventListener("click", () => file.click());
  file.addEventListener("change", async () => {
    const chosen = file.files?.[0];
    file.value = "";
    if (!chosen) return;
    try {
      picture = await pickImage(chosen);
      paintPicture();
    } catch (error) {
      problem(error, "couldn't open that picture");
    }
  });
  byId("feed-remove-picture").addEventListener("click", () => {
    picture = "";
    paintPicture();
  });
  byId("feed-open-upskill").addEventListener("click", () => showView("japanese"));
  byId("feed-lightbox").addEventListener("click", () => {
    byId("feed-lightbox").hidden = true;
  });
  onAwards(() => {
    if (isViewActive("feed")) void paintProgress();
  });
  onViewShown("feed", () => {
    seenAtOpen = lastSeen();
    editing = "";
    void loadAwards();
    void loadFeed(true).then(paint);
    void paintProgress();
  });
}
